import { getMetadata, ref } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import type { WpImportProperty } from './types';

export function toWpUploadsRelativePath(rawUrlOrPath: string): string {
  const v = rawUrlOrPath.trim();
  if (!v) return '';
  if (!v.startsWith('http')) {
    return v
      .replace(/^\//, '')
      .replace(/^wp-content\/uploads\//, '')
      .trim();
  }
  try {
    const u = new URL(v);
    return u.pathname
      .replace(/^\/+/, '')
      .replace(/^wp-content\/uploads\//, '')
      .trim();
  } catch {
    const idx = v.indexOf('/wp-content/uploads/');
    if (idx >= 0) return v.slice(idx + '/wp-content/uploads/'.length).replace(/^\//, '').trim();
    return v
      .replace(/^https?:\/\/[^/]+\//, '')
      .replace(/^wp-content\/uploads\//, '')
      .trim();
  }
}

function isLikelyImageUrl(url: string): boolean {
  return /\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?(#.*)?$/i.test(url.trim());
}

export interface StorageMirrorImageCheckResult {
  wpPostId: string;
  postTitle: string;
  checked: number;
  found: number;
  missing: { objectPath: string; from: string }[];
}

export async function checkStorageMirrorImagesForProperties(
  properties: WpImportProperty[],
  storageUploadsPrefix: string
): Promise<StorageMirrorImageCheckResult[]> {
  const prefix = storageUploadsPrefix.replace(/\/+$/, '');
  const out: StorageMirrorImageCheckResult[] = [];

  for (const pr of properties) {
    if (!pr.selected) continue;

    const imgs = pr.images.filter((im) => pr.selectedImageIds.has(im.id) && isLikelyImageUrl(im.url));
    const missing: { objectPath: string; from: string }[] = [];
    let found = 0;

    for (const im of imgs) {
      const rel = toWpUploadsRelativePath(im.raw || im.url);
      if (!rel) {
        missing.push({ objectPath: '(empty)', from: im.url });
        continue;
      }
      const objectPath = `${prefix}/${rel}`.replace(/\/{2,}/g, '/');
      try {
        await getMetadata(ref(storage, objectPath));
        found += 1;
      } catch {
        missing.push({ objectPath, from: im.url.slice(0, 180) });
      }
    }

    out.push({
      wpPostId: pr.wpPostId,
      postTitle: pr.postTitle,
      checked: imgs.length,
      found,
      missing,
    });
  }

  return out;
}
